/* eslint-disable @typescript-eslint/no-explicit-any */
import Table from '@/shared/components/Table/Table'

import { FILTER_KEYS, TABLE_KEYS } from '@/shared/components/Table/models/Filter.model'
import { useTableFilterStore } from '@/shared/components/Table/store/useTableFilterStore'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LanguageModel, OptionType } from '@/shared/api/BaseAPI.model'
import { useToppingFormConfig } from '@/features/menu-management/Toppings/hooks/useToppingFormConfig'
import { useDishCategoriesQuery } from '@/features/menu-management/SidebarAccordion/hooks/useDishCategoriesQuery'
import { useSelectedLanguagesQuery } from '@/features/menu-management/SidebarAccordion/hooks/useSelectedLanguagesQuery'
import { useToppingsDetailsStore } from '@/features/menu-management/Toppings/store/useToppingDetailsStore'
import { useToppingsQuery } from '@/features/menu-management/Toppings/hooks/useToppingsQuery'
import { useToppingsColumnsConfig } from '@/features/menu-management/Toppings/hooks/useToppingsColumnsConfig'
import { useIngredientsQuery } from '@/features/menu-management/Ingredients/hooks/useIngredientsQuery'
import DetailsModal from '@/shared/components/Modal/DetailsModal/DetailsModal'
import styles from '@/features/menu-management/Toppings/ToppingsManagement.module.scss'
import { useFormDirtyStore } from '@/shared/store/useFormDirtyStore'
import { formatPrice, formatPriceForInput, majorToMinor } from '@/shared/utils/price'

const onSubmitPayload = (data: any, selectedLanguagesList: LanguageModel[]) => {
  const { price, tax } = data
  const translations = selectedLanguagesList.map(lang => ({
    languageCode: lang.languageCode,
    description: data.toppingDescription[lang.languageCode],
    name: data.toppingName[lang.languageCode]
  }))

  const dishCategoryIds = data.dishCategories.map(
    (category: OptionType) => category.value
  )

  const ingredients = (data.ingredients || []).map((row: any) => ({
    id: row.id,
    unit: {
      id: row.unit || '',
      quantity: Number(row.quantity) || 0
    }
  }))

  const payload = {
    price: majorToMinor(price) ?? 0,
    taxRate: tax === '' || tax === undefined ? undefined : Number(tax),
    translations,
    dishCategoryIds,
    ingredients
  }
  return payload
}

function ToppingsManagement() {
  const { dishCategoriesList, dishCategoriesOptions } = useDishCategoriesQuery()
  const currentLanguage =
    useTranslation().i18n.language || localStorage.getItem('i18nextLng') || ''
  const { defaultLanguageOption, selectedLanguagesList } =
    useSelectedLanguagesQuery(currentLanguage)
  const { t } = useTranslation()
  const [toppingId, setToppingId] = useState<string>()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const configuredToppingIdRef = useRef<string>()
  const resetIsDirty = useFormDirtyStore(s => s.reset)

  useEffect(() => resetIsDirty(), [])

  const {
    toppingsList,
    toppingsListLoading,
    topping,
    postTopping,
    putTopping,
    deleteToppings
  } = useToppingsQuery(toppingId)

  const { ingredientsList } = useIngredientsQuery()

  const { filters, setFilter } = useTableFilterStore()
  const filterLanguageCode = filters.toppings?.[FILTER_KEYS.LANGUANGE_FILTER]
  const mappedToppingsList = toppingsList?.map(topping => ({
    id: topping.id,
    name:
      topping.translations.find(
        translation => filterLanguageCode?.value === translation.languageCode
      )?.name || '',
    description:
      topping.translations.find(
        translation => filterLanguageCode?.value === translation.languageCode
      )?.description || '',
    price: formatPrice(topping.price),
    categories:
      dishCategoriesList.data
        ?.filter(c => topping.dishCategoryIds.includes(c.id ?? ''))
        .map(c => ({
          id: c.id,
          label:
            c.translations.find(
              tr => filterLanguageCode?.value === tr.languageCode
            )?.name || ''
        })) || []
  }))

  const handleOnDeleteItem = (id: string) => {
    deleteToppings([id])
  }

  const handleOnDeleteItems = (ids: string[]) => {
    deleteToppings(ids)
  }

  const handleEditTopping = (id: string) => {
    setToppingId(id)
  }

  const columns = useToppingsColumnsConfig(
    handleEditTopping,
    handleOnDeleteItem
  )

  const columnsModel = ['name', 'description', 'price', 'categories']
  const filtersList = [
    FILTER_KEYS.LANGUANGE_FILTER,
    FILTER_KEYS.CATEGORIES_FILTER
  ]

  const defaultValues = useMemo(
    () => ({
      toppingLanguage: defaultLanguageOption as OptionType,
      toppingName: selectedLanguagesList.reduce(
        (acc, lang) => {
          acc[lang.languageCode] = ''
          return acc
        },
        {} as Record<string, string>
      ),
      toppingDescription: selectedLanguagesList.reduce(
        (acc, lang) => {
          acc[lang.languageCode] = ''
          return acc
        },
        {} as Record<string, string>
      ),
      price: undefined,
      tax: undefined
    }),
    [defaultLanguageOption, selectedLanguagesList]
  )

  const { setSelectedLanguage } = useToppingsDetailsStore()

  const initialFormConfig = useToppingFormConfig()

  const [formConfig, setFormConfig] = useState(initialFormConfig)

  const handleAddTopping = () => {
    const onSubmit = (data: any) => {
      postTopping(onSubmitPayload(data, selectedLanguagesList))
      resetIsDirty()
      setIsModalOpen(false)
    }

    setFormConfig({
      ...initialFormConfig,
      defaultValues,
      onSubmit
    })
    setIsModalOpen(true)
  }

  useEffect(() => {
    if (
      topping.data &&
      toppingId &&
      toppingId !== configuredToppingIdRef.current
    ) {
      configuredToppingIdRef.current = toppingId

      const onSubmit = (data: any) => {
        const payload = onSubmitPayload(data, selectedLanguagesList)
        putTopping({
          toppingPayload: payload,
          toppingId
        })
      }

      const defaultToppingsNames = topping.data?.translations.reduce(
        (acc, item) => {
          acc[item.languageCode] = item.name
          return acc
        },
        {} as Record<string, string>
      )

      const defaultToppingsDescriptions = topping.data?.translations.reduce(
        (acc, item) => {
          acc[item.languageCode] = item.description || ''
          return acc
        },
        {} as Record<string, string>
      )

      const defaultPrice = formatPriceForInput(topping.data?.price)

      const defaultDishCategories = dishCategoriesOptions.filter(category =>
        topping.data?.dishCategoryIds.includes(category.value)
      )

      const toppingFromList = toppingsList?.find(t => t.id === toppingId)
      const defaultToppingIngredients = (
        toppingFromList?.ingredients || []
      ).map(ingredient => {
        const match = ingredientsList?.find(i => i.id === ingredient.id)
        return {
          value: ingredient.id,
          label:
            match?.translations?.find(t => t.languageCode === 'en')?.name ||
            ingredient.id
        }
      })

      const defaultIngredientRows = (toppingFromList?.ingredients || []).map(
        ingredient => {
          const match = ingredientsList?.find(i => i.id === ingredient.id)
          return {
            id: ingredient.id,
            toppingIngredients:
              match?.translations?.find(t => t.languageCode === 'en')?.name ||
              ingredient.id,
            quantity: String(ingredient.unit?.quantity ?? ''),
            unit: ingredient.unit?.id ?? ''
          }
        }
      )

      const newDefaultValues = {
        toppingLanguage: defaultLanguageOption,
        toppingName: defaultToppingsNames,
        toppingDescription: defaultToppingsDescriptions,
        price: defaultPrice,
        tax: topping.data?.taxRate ?? '',
        dishCategories: defaultDishCategories,
        toppingIngredients: defaultToppingIngredients,
        ingredients: defaultIngredientRows
      }

      const newFormConfig = {
        ...initialFormConfig,
        defaultValues: newDefaultValues,
        onSubmit
      }

      setFormConfig(newFormConfig)
      setIsModalOpen(true)
    }
  }, [
    topping.data,
    toppingId,
    toppingsList,
    ingredientsList,
    selectedLanguagesList,
    putTopping,
    initialFormConfig,
    defaultLanguageOption,
    dishCategoriesOptions
  ])

  const mappedCategoriesFilters = Array.isArray(
    filters.toppings?.['categories-filter']
  )
    ? filters.toppings['categories-filter'].map(category => category.label)
    : []
  const mappedCategoriesFiltersSet = new Set(mappedCategoriesFilters)
  const filteredToppingsRows = (mappedToppingsList || []).filter(topping => {
    if (!mappedCategoriesFilters.length) {
      return true
    }
    const rowCategoryLabels = Array.isArray(topping.categories)
      ? topping.categories.map(category => category.label)
      : []
    return rowCategoryLabels.some(label => mappedCategoriesFiltersSet.has(label))
  })

  useEffect(() => {
    if (defaultLanguageOption) {
      setFilter(
        TABLE_KEYS.TOPPINGS,
        FILTER_KEYS.LANGUANGE_FILTER,
        defaultLanguageOption
      )
    }
  }, [defaultLanguageOption, setFilter])

  useEffect(() => {
    setSelectedLanguage(defaultLanguageOption)
  }, [defaultLanguageOption, setSelectedLanguage])

  if (!defaultLanguageOption) {
    return null
  }

  const handleOnModalClose = () => {
    configuredToppingIdRef.current = undefined
    setToppingId(undefined)
    setIsModalOpen(false)
  }

  return (
    <div className={styles.toppingsTableWrapper}>
      <Table
        columns={columns}
        columnsModel={columnsModel}
        filtersList={filtersList}
        tableKey={TABLE_KEYS.TOPPINGS}
        initialRows={filteredToppingsRows}
        filterModel={{ items: [] }}
        handleOnAddItem={handleAddTopping}
        handleOnDeleteItems={handleOnDeleteItems}
        isLoading={toppingsListLoading}
      />
      <DetailsModal
        title={t('TOPPING_DETAILS_MODAL.TITLE')}
        formConfig={formConfig}
        onClose={handleOnModalClose}
        isOpen={isModalOpen}
      />
    </div>
  )
}

export default ToppingsManagement
